import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthInput from './AuthInput';
import AuthPasswordInput from './AuthPasswordInput';
import Checkbox from '../../shared/forms/Checkbox';
import VerifyModal from './VerifyModal';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { ReactComponent as Spinner } from '../../assets/icons/spinner2.svg';
import { ReactComponent as DownIcon } from '../../assets/icons/downArrow.svg';
import { useMobile } from '../../contexts/MobileContext';
import { tiers } from '../../shared/config/tiers';
import { useMessage } from '../../contexts/MessageContext';

import Image from '../../assets/images/signup-image.png';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const SignupForm = () => {
  const { isMobile } = useMobile();
  const showMessage = useMessage();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [costCap, setCostCap] = useState(450);
  const [company, setCompany] = useState('');
  const [telephone, setTelephone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accept, setAccept] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = React.useState(false);

  const validEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const getCssToken = (token, fallback) => {
    if (typeof window === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return value || fallback;
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: getCssToken('--font-normal', '1rem'),
        color: getCssToken('--input-text', '#151c2d'),
        iconColor: getCssToken('--input-text', '#151c2d'),
        '::placeholder': {
          color: getCssToken('--input-placeholder', '#67758f'),
        },
      },
      invalid: {
        color: getCssToken('--warning', '#d42c2c'),
      },
    },
  };

  function getEmailWarning (email) {
    if (!email || email.trim().length === 0) {
      return 'Email is required';
    }

    if (!validEmailRegex.test(email) ||
        email.includes('..')) {
      return 'Invalid email';
    }

    const [localPart, domain] = email.split('@');

    if (!localPart || !domain ||
        localPart.startsWith('.') ||
        localPart.endsWith('.') ||
        !domain.includes('.') ||
        domain.startsWith('.') ||
        domain.endsWith('.')) {
      return 'Invalid email';
    }

    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) {
      return 'Invalid email';
    }

    return '';
  }

  const validNameRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ' -]{2,50}$/;

  function getNameWarning (name) {
    if (!name || name.trim().length === 0) {
      return 'Name is required';
    }

    name = name.trim();

    if (name.length < 2) {
      return 'Name is too short';
    }

    if (name.length > 50) {
      return 'Name is too long';
    }

    if (validNameRegex.test(name)) {
      return '';
    }
    else {
      return 'Invalid name';
    }
  }

  function getCompanyWarning (name) {
    if (!name || name.trim().length === 0) {
      return 'Company name is required';
    }

    name = name.trim();

    if (name.length < 2) {
      return 'Company name is too short';
    }

    if (name.length > 50) {
      return 'Company name is too long';
    }

    if (validNameRegex.test(name)) {
      return '';
    }
    else {
      return 'Invalid name';
    }
  }

  function getPhoneWarning (phoneNumber) {
    if (!phoneNumber || phoneNumber.trim().length === 0) {
      return 'Telephone number is required';
    }

    return '';
  }

  function getPasswordWarning (password, target) {
    if (password.length < 8) {
      return 'Password is less than 8 characters';
    }
    else if (!password.match(/[A-Z]/)) {
      return 'Password does not contain an uppercase letter';
    }
    else if (!password.match(/[0-9]/)) {
      return 'Password does not contain a number';
    }
    else if (target && password !== target) {
      return 'Passwords do not match';
    }
    else {
      return '';
    }
  }

  const emailExists = async (email) => {
    const response = await fetch(process.env.REACT_APP_SERVER_URL + '/api/existingEmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })});
    const data = await response.json();
    return data.exists;
  }

  const create = async () => {
    setProcessing(true);

    if (getEmailWarning(email)) {
      showMessage('Please enter a valid email address.');
      setProcessing(false);
      return;
    }

    let exists = await emailExists(email);

    if (exists) {
      showMessage('An account with this email already exists.');
      setProcessing(false);
      return;
    }

    if (getNameWarning(name)) {
      showMessage('Please enter a valid full name.');
      setProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name
      },
    });
    if (error) {
      showMessage('Please enter a valid credit card number.');
      setProcessing(false);
      return;
    }

    if (getCompanyWarning(company)) {
      showMessage('Please enter a valid company name.');
      setProcessing(false);
      return;
    }

    if (getPhoneWarning(telephone)) {
      showMessage('Please enter a valid telephone number.');
      setProcessing(false);
      return;
    }

    if (getPasswordWarning(password, null)) {
      showMessage('Please enter a valid password.');
      setProcessing(false);
      return;
    }

    if (getPasswordWarning(confirmPassword, password)) {
      showMessage('Passwords do not match.');
      setProcessing(false);
      return;
    }

    if (!accept) {
      showMessage('You must accept the Terms of Use and Privacy Policy.');
      setProcessing(false);
      return;
    }

    fetch(process.env.REACT_APP_SERVER_URL + '/api/signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, name, costCap, company,
                             telephone, password,
                             paymentMethodId: paymentMethod.id })})
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setProcessing(false);
          setModalVisible(true);
        }
      });
  }

  return (
    <div className={`login${isMobile ? ' single' : ''}`}>
      {modalVisible && (
        <VerifyModal setVisible={setModalVisible}/>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}>

        <h1>Create account</h1>

        <AuthInput label="Email"
               placeholder='Enter your email'
               getWarning={getEmailWarning}
               type='email'
               onChange={(e) => {
                 setEmail(e.target.value);
               }}/>

        <AuthInput label="Full name"
               placeholder='Enter your full name'
               getWarning={getNameWarning}
               onChange={(e) => {
                 setName(e.target.value);
               }}/>

        <div className="input input-auth">
          <span className="input-label">Monthly cost cap</span>
          <div className="input-row">
            <div style={{position: 'relative', width: '100%'}}>
              <select className="input-field input-field-auth"
                      style={{width: '100%',
                              paddingRight: '2.75rem',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                             }}
                      onChange={(e) => {
                        setCostCap(parseFloat(e.target.value));
                      }}>
                {tiers.map((tier) => {
                  let price = Math.round(tier.maxTraffic * tier.price);
                  return <option value={price}>${price}</option>
                })}
              </select>
              <DownIcon className="icon sm"
                        style={{position: 'absolute',
                                right: '1.25rem',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                width: '1.2rem',
                                height: '1.2rem',
                                stroke: 'var(--text-contrast-high)',
                                fill: 'none',
                                zIndex: 1,
                               }}/>
            </div>
          </div>

          <span className="help-text">
            Minimum charge is $450 per month
          </span>
          <span className="help-text">
            Use our <a href="https://polygraph.net/pricing/"
                       target="_blank"
                       rel="noopener noreferrer"
                       className="custom-link">calculator</a> to estimate your monthly cost
          </span>
        </div>

        <div className="input input-auth">
          <span className="input-label">Payment details</span>
          <div className="input-row">
            <div className="input-field"
                 style={{display: 'flex',
                         alignItems: 'center',
                         width: '100%',
                         minHeight: '3rem',
                         padding: '0 var(--gap)',
                         boxSizing: 'border-box',
                        }}>
              <div style={{flex: '1 1 auto', minWidth: 0}}>
                <CardElement options={cardElementOptions} />
              </div>
            </div>
          </div>
        </div>

        <AuthInput label="Company name"
               placeholder='Enter your company name'
               getWarning={getCompanyWarning}
               onChange={(e) => {
                 setCompany(e.target.value);
               }}/>

        <AuthInput label="Telephone"
               placeholder='Enter your telephone number'
               getWarning={getPhoneWarning}
               onChange={(e) => {
                 setTelephone(e.target.value);
               }}/>

        <AuthPasswordInput label="New Password"
                       placeholder='Enter new password'
                       showWarning={true}
                       onChange={(e) => {
                         setPassword(e.target.value);
                       }}/>

        <AuthPasswordInput label="Confirm Password"
                       placeholder='Confirm new password'
                       showWarning={true}
                       target={password}
                       onChange={(e) => {
                         setConfirmPassword(e.target.value);
                       }}/>

        <div style={{display: 'flex',
                     gap: 'var(--gap-half)',
                     alignItems: 'center',
                    }}>
          <Checkbox
            checked={accept}
            onChange={(e) => {
              setAccept(e.target.checked);
            }}/>

          <span style={{color: 'var(--text-contrast-lower)',
                        fontWeight: '400',
                        fontSize: 'var(--font-sm)',
                       }}>
            I accept the <Link className='custom-link' to='https://polygraph.net/terms-of-use'>Terms of Use</Link> and <Link className='custom-link' to='https://polygraph.net/privacy-policy'>Privacy Policy</Link>.
          </span>
        </div>

        <button className="button primary auth-button">
          {processing && (
            <Spinner style={{
                       width: '15px',
                       height: '15px',
                       animation: 'spin 1s linear infinite',
                     }} />
          )}
          {!processing && 'Create account'}
        </button>

        <p>
          Already have an account?
          <Link className='custom-link auth-link' to='/'>Log In</Link>
        </p>
      </form>

      {!isMobile && (
        <img src={Image}
             style={{width: '100%',
                     height: '100%',
                     objectFit: 'cover',
                     borderRadius: '5px',
                    }}
             alt=""/>
      )}
    </div>
  );
}

const SignupPage = () => {
  return (
    <Elements stripe={stripePromise}>
      <SignupForm/>
    </Elements>
  );
}

export default SignupPage;
